// KEY CAPACITY — how many model streams one BYOK key sustains at once, and whether a
// task may start on it now (capacity-aware scheduling, #4912 revived by the owner on
// 2026-09-24: "if 1 sub it should do just 1 PR at a time").
//
// Pure: the assigner hands it the ranked pool, the in-flight units per key and the
// learned ceilings, and seals whatever it answers. No I/O, no clock.
//
// ── The three numbers, and which one binds ─────────────────────────────────────
//
//   declared — the owner's per-key override (`byok_keys.capacity`, `billing
//              key_capacity_set`). What the owner knows about the plan they bought.
//   default  — what the platform assumes when nobody declared: a SUBSCRIPTION key is
//              LOW ({@link SUBSCRIPTION_DEFAULT_CAPACITY}), a METERED key is unbounded
//              (its ceiling is whatever the provider's rate limit turns out to be).
//   learned  — the operating point telemetry found (`key_capacity_learned:<keyId>`,
//              `@developerz/queue`): +1 per 10 clean saturated minutes (each raise
//              records the value it left as PROVEN); halved, once per 5-minute
//              episode, only by a rate-limit cool the PROVIDER refused (a transport
//              timeout or an unattributed report cools the key and teaches nothing),
//              and never below proven unless the refusal lands at or under it.
//              Below the ceiling it clamps the PLAN; from the ceiling up to
//              the probe ({@link learnedBounds}) it is the REVIEW RESERVE's operating
//              point ({@link reviewReserveWidth}), and it stays at the probe.
//
// The CEILING is `declared ?? default` — a statement about the plan. The EFFECTIVE
// capacity is `min(ceiling, learned)`: a 429 at or under the plan can pull a key under
// what its plan says (one while the key runs OVER the plan only shrinks the review
// reserve, `@developerz/queue`'s plan floor), and a quiet hour cannot push it past it. So a z.ai coding sub nobody declared stays at 1
// however long it runs clean, and the owner who bought a Max plan says so once. The one
// stream source over the plan is a review's reserve, learned by the same loop.
//
// ── The unit ───────────────────────────────────────────────────────────────────
//
// One unit = one concurrent model stream on the key. A PR-producing (harness) task
// holds as many units as its editor fan-out may run at once — which is exactly what
// admission hands it as `budget`, so the two cannot disagree. A review holds its
// reviewer wave's width out of the free units ({@link REVIEW_TARGET_WIDTH}), or one
// stream in the reserve; a scout or a setup session holds one. See {@link KEY_ADMISSION_LANE}.
//
// ── The coding width is a SHARE, not the headroom ─────────────────────────────
//
// Owner, 2026-09-27: "the idea is we do not just prs and reviews in parallel … we also
// do hive mind" — several hive PRs on one wide subscription. A coding task that took
// every free unit made the first claim on a 16-unit sub hold all 16, so the next coding
// task found the key full: one PR at a time however wide the plan bought. So a coding
// task CONTENDED by other waiting coding work takes {@link codingFairShare}; a lone one
// takes the free units up to {@link DEFAULT_TEAM_MAX_EDITORS}, the most a box runs.

import { z } from 'zod';
import { type BYOKKey, BYOKKeyKind, BYOKProvider } from '../entities/byok';
import { KeyAdmissionLane } from './key-capacity-rank';
import { isFlatRateKey } from './llm-credential';
import { TaskOrigin } from './task-envelope';

// The lane and rank tables (`KeyAdmissionLane`, `AdmissionRank`, the reservation) live
// beside this module; every name keeps this import path.
export * from './key-capacity-rank';

/**
 * The declared-capacity write range: `billing key_capacity_set`, the REST body and
 * `byok_keys_capacity_ck`. One unit is the floor (zero would be a benched key, which
 * `active = false` already says); the top is a typo guard, far past any plan we have
 * seen (Kimi Code documents 30 concurrent requests as its ceiling).
 */
export const KEY_CAPACITY_MIN = 1;
export const KEY_CAPACITY_MAX = 256;

/**
 * The one declared-capacity WRITE value every door parses — the REST body
 * (`PATCH /v1/billing/keys/:id/capacity` + its `/v1/me` mirror), the MCP
 * `key_capacity_set` input and the `@developerz/db` seam that re-checks it before
 * its statement. An integer in [{@link KEY_CAPACITY_MIN}, {@link KEY_CAPACITY_MAX}],
 * or `null` to CLEAR the declaration so the default for the key's kind applies again.
 */
export const KeyCapacityDeclarationSchema = z
  .number()
  .int()
  .min(KEY_CAPACITY_MIN)
  .max(KEY_CAPACITY_MAX)
  .nullable();

/**
 * The default for a subscription key per provider, cited, because a guessed number
 * here is a throughput cap or a 1302 storm on every account that never declared one.
 *
 * - `zai` — 1. z.ai ties coding-plan concurrency to the tier and adjusts it
 *   dynamically ("Max > Pro > Lite"), recommending Lite for "a single project at a
 *   time", Pro for "1–2 projects", Max for "2 or more"
 *   (https://docs.z.ai/devpack/usage-policy, read 2026-09-24). No number per tier is
 *   published, and the key does not say which tier bought it — so the lowest tier's
 *   recommendation is the default and a Pro/Max owner declares theirs.
 * - `kimi` — 2. Kimi Code advertises "up to 30 concurrent requests" without mapping it
 *   to a tier (kimi.com product overview, read 2026-09-24), and a coding run is many
 *   requests. The owner's conservative range for an undocumented plan is 1–2 runs.
 *
 * Any other provider's subscription (the team gateway, the Model Studio Token Plan)
 * takes {@link SUBSCRIPTION_DEFAULT_CAPACITY}.
 */
export const SUBSCRIPTION_CAPACITY_BY_PROVIDER: Readonly<Partial<Record<BYOKProvider, number>>> = {
  [BYOKProvider.Zai]: 1,
  [BYOKProvider.Kimi]: 2,
};

/** A subscription whose provider documents nothing: one stream, the owner raises it. */
export const SUBSCRIPTION_DEFAULT_CAPACITY = 1;

/**
 * The reserved slot a REVIEW may take past the key's capacity. Decided, not defaulted:
 * at capacity 1 a review queued FIFO behind the coding backlog leaves every opened PR
 * unreviewed until the backlog drains — measured on tesote 2026-09-24, the pending
 * coding tasks were minted before the reviews of the PRs they would stack on. A review
 * is the step that FINISHES a PR, so it may start while one coding task holds the key,
 * never a second coding task. One stream over the plan is what the per-box AIMD gate
 * (#4913) paces in-step: after it rolled out, tesote's sub key ran a peak of 19
 * concurrent calls with 0 z.ai 1302s in the four hours to 19:00Z.
 *
 * Since #6228 this is the reserve's FLOOR, the width a key that a provider refused falls
 * back to; a clean key's reserve is {@link reviewReserveWidth}.
 */
export const REVIEW_RESERVED_UNITS = 1;

/**
 * How many streams past its plan a REVIEW may take on a key whose plan units are held —
 * the reviewer wave's width, at most doubling the plan: `min(reviewWidth, ceiling)`,
 * floored at {@link REVIEW_RESERVED_UNITS}. After a provider refusal it is narrower:
 * `max(REVIEW_RESERVED_UNITS, learned − ceiling)` while the learned record sits at or
 * past the plan and short of {@link learnedBounds}' probe, and one stream with the
 * record under the plan; at the probe it is the widest again (#6228). `null` for an
 * unbounded key: there is no plan to run past, so no reserve (and the audit records
 * none). The reserve only ever runs PAST the plan (`reviewGrant`): never on held-back
 * or contended plan units.
 *
 * WHY WIDER THAN ONE. Two measurements, both prod, read-only (`bun run infra psql`):
 *
 * - OVERALL (`review_fanout` rows, `max_reviewers` = 1 against all, 7 days):
 *   `SELECT count(*) FILTER (WHERE (payload->>'max_reviewers')::int = 1), count(*) FROM
 *   task_run_events WHERE kind = 'status' AND ts > now() - interval '7 days' AND
 *   payload->>'event' = 'review_fanout'` — 751 of 1,481 in #6228's read (7 days to
 *   2026-10-12), 745 of 1,475 re-read 2026-10-13 00:06Z.
 * - BY KEY (`review_brain.stream_budget` joined to the latest `task.assigned` row's
 *   `output.llm.key_id` per task, 7 days to 2026-10-12): 688 of the 749 width-1 reviews
 *   were on keys whose owner DECLARED the plan (z.ai 16: 203 of 367, MiniMax 4: 224 of
 *   237, z.ai 2: 261 of 261), none holding a learned record when read; the MiniMax key,
 *   read live, held four coding claims on its four units. Query: `WITH rb AS (SELECT
 *   task_id, payload->>'stream_budget' sb FROM task_run_events WHERE kind = 'status' AND
 *   ts > now() - interval '7 days' AND payload->>'event' = 'review_brain'), asg AS (SELECT
 *   DISTINCT ON (input->>'taskId') input->>'taskId' tid, output->'llm'->>'key_id' key_id
 *   FROM audit_events WHERE kind = 'task.assigned' AND ts > now() - interval '8 days'
 *   ORDER BY input->>'taskId', ts DESC) SELECT a.key_id, rb.sb, count(*) FROM rb LEFT JOIN
 *   asg a ON a.tid = rb.task_id GROUP BY 1, 2`.
 *
 * A width-1 wave runs serially ({@link REVIEW_TARGET_WIDTH}: p50 11.0 vs 7.6 min). Those
 * keys' provider-refused `rate_limited` cools over 14 days (`dispatch.key_cooled`): MiniMax
 * 0, z.ai 16 five (all 2026-09-30..10-01), z.ai 2 none attributed.
 *
 * THE FAILURE THIS BOUND PREVENTS: the plan's own concurrency limit answering 429. Past
 * the plan is the one place admission puts streams the owner did not declare, so it is
 * (a) at most the plan again, (b) never more than one wave uses, and (c) LEARNED by the
 * `@developerz/queue` learner: a provider refusal while the key is over its plan steps
 * the record back toward the plan and never below it, so only the reserve shrinks; the
 * reserve re-opens one stream per clean saturated interval, after a hold, as the record
 * climbs to the probe ceiling ({@link learnedBounds}). A key at plan `1` keeps a
 * one-stream reserve: an undeclared z.ai Lite sub is exactly as before.
 */
export function reviewReserveWidth(
  capacity: Pick<KeyCapacity, 'declared' | 'default' | 'learned'>,
  reviewWidth: number,
): number | null {
  const ceiling = capacity.declared ?? capacity.default;
  if (ceiling === null) return null;
  const widest = Math.max(REVIEW_RESERVED_UNITS, Math.min(reviewWidth, ceiling));
  if (capacity.learned === null) return widest;
  return Math.min(widest, Math.max(REVIEW_RESERVED_UNITS, capacity.learned - ceiling));
}

/**
 * What the learner is told about a bounded key on every RAISE (`observeSaturated`, both
 * admitters): its `plan` (`declared ?? default`), the floor a refusal while the key ran
 * OVER the plan never goes below, and its `probe`, the plan plus the widest review
 * reserve, where the record stops climbing and stays (`@developerz/queue`
 * `raiseLearnedCapacity`). `null` for an unbounded key: nothing past a plan that does not
 * exist, and no floor.
 */
export type LearnedBounds = { readonly plan: number; readonly probe: number };

export function learnedBounds(
  capacity: Pick<KeyCapacity, 'declared' | 'default'>,
): LearnedBounds | null {
  const plan = capacity.declared ?? capacity.default;
  if (plan === null) return null;
  return {
    plan,
    probe: plan + Math.max(REVIEW_RESERVED_UNITS, Math.min(REVIEW_TARGET_WIDTH, plan)),
  };
}

/**
 * The widest a review is granted from a bounded key's FREE plan units: the reviewer
 * wave's own ceiling (`DEFAULT_REVIEW_MAX_REVIEWERS`, `apps/runner`; a test there pins
 * the two equal), so a unit granted past it is never streamed. A review used to be sized to
 * ONE stream whatever the key had free, which ran its reviewer wave SERIALLY: measured
 * 2026-09-29 (prod `task_run_events` `review_fanout`, 7 days), tesote.ai's 16+-file
 * reviews at `max_reviewers` 1 took p50 11.0 / p90 28.3 min against 7.6 / 13.4 at 4 on
 * the same model, and #2762's review ran 8 members one after another for 58 min.
 */
export const REVIEW_TARGET_WIDTH = 4;

/**
 * How wide a `Review`-lane origin runs: a PR review fans its reviewer wave out
 * ({@link REVIEW_TARGET_WIDTH}); a reply on one of its threads is one stream, and a unit
 * it would hold idle is a unit a PR could have run on.
 */
export function reviewAdmissionWidth(origin: TaskOrigin): number {
  return origin === TaskOrigin.PrReview ? REVIEW_TARGET_WIDTH : 1;
}

/**
 * The editor fan-out's concurrency default — `resolveMaxEditors` (`apps/runner`,
 * `team/fanout/plan.ts`) falls back to it, and it is the WIDEST a coding claim on a
 * bounded key is granted: the box narrows the budget to its own cap
 * (`budgetedWidth`, `llm/stream-budget.ts`), so a unit granted past it is held and never
 * streamed — idle headroom another PR could have run on. Measured 2026-09-27 (prod,
 * `task_run_events` `team_fanout`, 14 days): 1,315 fan-outs, every one on a box at
 * `max_editors` 10 or narrowed to 1 by a capacity-1 key; none wider.
 *
 * A CONCURRENCY cap, not a count: a manifest may plan more editor tags and they all run
 * in waves; capping the count would drop declared files the guard then blocks as
 * phantom edits.
 */
export const DEFAULT_TEAM_MAX_EDITORS = 10;

/**
 * How many coding PRs a bounded key is shared between when coding work is waiting for
 * it: the fair share is `floor(capacity / CODING_TARGET_PARALLEL)`. Four, from the same
 * 14-day `team_fanout` read: 1,261 of 1,315 fan-outs (96%) dispatched 4 editors or
 * fewer, so on a 16-unit sub four PRs run side by side and 96% of them still run every
 * editor at once. A wider plan is never cut — its editors run in waves.
 */
export const CODING_TARGET_PARALLEL = 4;

/**
 * The floor of a contended coding task's share: a width of one is not a hive. Two covers
 * 1,156 of 1,315 fan-outs (88%) at full width in the same read; below capacity
 * `2 × CODING_TARGET_PARALLEL` the floor, not the quotient, sets the share. Never more
 * than is free: the floor sizes a share, it does not admit past a full key.
 */
export const CODING_MIN_WIDTH = 2;

/** A contended coding task's width on a key of `capacity`, before what is free clamps it. */
export function codingFairShare(capacity: number): number {
  return Math.min(
    DEFAULT_TEAM_MAX_EDITORS,
    Math.max(CODING_MIN_WIDTH, Math.floor(capacity / CODING_TARGET_PARALLEL)),
  );
}

/** Which number decided a key's effective capacity — rides every view and hold row. */
export const KeyCapacitySource = {
  Declared: 'declared',
  Default: 'default',
  Learned: 'learned',
  /** Metered, nothing declared, nothing learned: no concurrency bound is known. */
  Unbounded: 'unbounded',
} as const;
export type KeyCapacitySource = (typeof KeyCapacitySource)[keyof typeof KeyCapacitySource];

/** A key's capacity, every number that fed it, and which one binds. `null` = unbounded. */
export type KeyCapacity = {
  readonly effective: number | null;
  readonly source: KeyCapacitySource;
  readonly declared: number | null;
  readonly default: number | null;
  readonly learned: number | null;
};

/** The platform's assumption for a key nobody declared — see the per-provider table. */
export function defaultKeyCapacity(
  key: Pick<BYOKKey, 'provider' | 'baseUrl' | 'kind'>,
): number | null {
  if (!isFlatRateKey(key)) return null;
  return SUBSCRIPTION_CAPACITY_BY_PROVIDER[key.provider] ?? SUBSCRIPTION_DEFAULT_CAPACITY;
}

/**
 * The ceiling (`declared ?? default`) clamped by what telemetry learned. A learned
 * value at or above the ceiling does not bind, so the source stays the ceiling's.
 */
export function effectiveKeyCapacity(
  key: Pick<BYOKKey, 'provider' | 'baseUrl' | 'kind'> & {
    readonly capacity?: number | null | undefined;
  },
  learned: number | null | undefined,
): KeyCapacity {
  const declared = key.capacity ?? null;
  const dflt = defaultKeyCapacity(key);
  const ceiling = declared ?? dflt;
  const learnedValue = learned ?? null;
  const base = { declared, default: dflt, learned: learnedValue };
  if (learnedValue !== null && (ceiling === null || learnedValue < ceiling)) {
    return { ...base, effective: learnedValue, source: KeyCapacitySource.Learned };
  }
  if (declared !== null)
    return { ...base, effective: declared, source: KeyCapacitySource.Declared };
  if (dflt !== null) return { ...base, effective: dflt, source: KeyCapacitySource.Default };
  return { ...base, effective: null, source: KeyCapacitySource.Unbounded };
}

/**
 * May a task of `lane` start on a key with `capacity` while `inflight` units run on
 * it — and with how many streams? `budget` is the fan-out width the box may use AND
 * the units the claim holds: the two are one number so they cannot disagree.
 *
 * - unbounded (`null`): admitted, no budget — the box keeps its own width and the
 *   claim counts one, exactly as before capacity existed.
 * - `Coding`: needs one free unit. Contended, its width is
 *   `min(free, codingFairShare(capacity))`; alone, `min(free, DEFAULT_TEAM_MAX_EDITORS)`.
 *   Either way the rest of the key stays claimable by the next coding task.
 * - `Review`: its PLAN grant, `min(planFree, width)`, where `planFree` is the plan units
 *   free after the hold-back, clamped at 0 (`reviewPlanFree`), and `width` is the
 *   review's wave, at the fair share when contended. It runs past the plan only once
 *   that grant took every physically free unit, and then by at most `reserveWidth`
 *   streams over `capacity` ({@link reviewGrant}). Held-back and
 *   contended plan units are never taken by the reserve; with none left to the review,
 *   it gets the one reserve slot. `reserved` holds back only free PLAN units — the
 *   reserve is review-only, so work that outranks a review but cannot run in it never
 *   parks the review behind itself. The key never holds more than
 *   `capacity + reserveWidth` units.
 * - `Single`: one free unit.
 */
export function admitOnKey(input: AdmitOnKeyInput): AdmitOnKeyVerdict {
  if (input.capacity === null) return { admitted: true, budget: null };
  const free = input.capacity - input.inflight - (input.reserved ?? 0);
  if (input.lane === KeyAdmissionLane.Coding) {
    if (free < 1) return { admitted: false };
    const width = input.contended ? codingFairShare(input.capacity) : DEFAULT_TEAM_MAX_EDITORS;
    return { admitted: true, budget: Math.min(free, width) };
  }
  if (input.lane === KeyAdmissionLane.Review) {
    const { budget } = reviewGrant({ ...input, capacity: input.capacity });
    return budget >= 1 ? { admitted: true, budget } : { admitted: false };
  }
  return free >= 1 ? { admitted: true, budget: 1 } : { admitted: false };
}

/** {@link admitOnKey}'s question. */
export type AdmitOnKeyInput = {
  readonly lane: KeyAdmissionLane;
  readonly capacity: number | null;
  readonly inflight: number;
  /**
   * Units held back for work that outranks this task and is WAITING for a slot
   * ({@link reservedForHigherRanks}). A bounded key only: an unbounded one has room
   * for everyone. Absent ⇒ 0.
   */
  readonly reserved?: number;
  /**
   * Other coding-lane work is waiting ({@link waitingCodingWork}), so a coding task takes
   * its fair share rather than the headroom. Read by the `Coding` lane only. Absent ⇒ false.
   */
  readonly contended?: boolean;
  /**
   * The widest a `Review`-lane task runs out of free plan units
   * ({@link reviewAdmissionWidth}). Absent ⇒ 1: one stream.
   */
  readonly reviewWidth?: number;
  /**
   * How many streams past the plan a `Review`-lane task may take
   * ({@link reviewReserveWidth}, which {@link admitOnPool} derives per key). Absent ⇒
   * {@link REVIEW_RESERVED_UNITS}.
   */
  readonly reserveWidth?: number;
};

/** {@link admitOnKey}'s answer: `budget` is the run's width AND the units its claim holds. */
export type AdmitOnKeyVerdict =
  | { readonly admitted: true; readonly budget: number | null }
  | { readonly admitted: false };

/** A review's plan units free after the hold-back for waiting work that outranks it. */
function reviewPlanFree(input: AdmitOnKeyInput & { readonly capacity: number }): number {
  // The reserve is REVIEW-ONLY: a waiting babysit (Coding) or CI watch (Single) can
  // never start in it, so holding it back for them leaves it idle while the review
  // waits on work that is itself waiting for a plan unit. What outranks a review holds
  // back only the plan units still free. Measured 2026-09-28 (developerz-ai, capacity-1
  // MiniMax sub): 2 babysits waiting ⇒ every review parked `key_saturated` all day.
  const planFree = Math.max(0, input.capacity - input.inflight);
  return planFree - Math.min(input.reserved ?? 0, planFree);
}

/**
 * A review's width, and how much of it is RESERVE (streams past the plan, or the one
 * reserve slot). The reserve exists ONLY past the plan (#6228 review):
 *
 * - The PLAN grant is the wave on the free plan units after the hold-back for waiting
 *   work that outranks the review, at the fair share when coding work waits.
 * - While plan units the review may not take remain (held back for a waiting babysit,
 *   or left to contended coding work), nothing goes past the plan: the review keeps its
 *   plan grant, or with none, the one reserve SLOT ({@link REVIEW_RESERVED_UNITS}, the
 *   pre-#6228 rule, which counts held-back units as occupied).
 * - Once the plan grant took every unit physically free, the rest of the wave may run
 *   past the plan: at most `reserveWidth` streams over `capacity` in all, and never more
 *   than the units OTHER claims hold (a lone review on an idle key stays on its plan;
 *   the reserve is for a plan others are using).
 *
 * So the key never holds more than `capacity + reserveWidth` units. `budget` 0 = refused.
 */
function reviewGrant(input: AdmitOnKeyInput & { readonly capacity: number }): {
  readonly budget: number;
  readonly reserve: number;
} {
  const want = input.reviewWidth ?? 1;
  const width = input.contended ? Math.min(want, codingFairShare(input.capacity)) : want;
  const physicallyFree = Math.max(0, input.capacity - input.inflight);
  const plan = Math.min(reviewPlanFree(input), width);
  if (plan < physicallyFree) {
    return plan >= 1 ? { budget: plan, reserve: 0 } : { budget: 1, reserve: 1 };
  }
  const overPlan = Math.max(0, input.inflight - input.capacity);
  const room = (input.reserveWidth ?? REVIEW_RESERVED_UNITS) - overPlan;
  const reserve = Math.max(0, Math.min(want - plan, room, input.inflight));
  return { budget: plan + reserve, reserve };
}

/**
 * How an ADMITTED claim got its width — the `path` of {@link KeyAdmissionAudit}: no bound
 * on the key, the plan's free units alone, or some of the review reserve past them.
 */
export const KeyAdmissionPath = {
  Unbounded: 'unbounded',
  Plan: 'plan',
  Reserve: 'reserve',
  /** No load read (a Dragonfly fault): admitted fail-open at the key's capacity. */
  Unobserved: 'unobserved',
} as const;
export type KeyAdmissionPath = (typeof KeyAdmissionPath)[keyof typeof KeyAdmissionPath];

/** Which path {@link admitOnKey} took to grant `budget` on this input. */
export function keyAdmissionPath(input: AdmitOnKeyInput, budget: number | null): KeyAdmissionPath {
  if (input.capacity === null || budget === null) return KeyAdmissionPath.Unbounded;
  if (input.lane !== KeyAdmissionLane.Review) return KeyAdmissionPath.Plan;
  return reviewGrant({ ...input, capacity: input.capacity }).reserve > 0
    ? KeyAdmissionPath.Reserve
    : KeyAdmissionPath.Plan;
}

/** A unit count that may be zero: units in flight, units held back. */
const countOrNull = z.number().int().min(0).nullable();
/** A capacity: one unit is the floor ({@link KEY_CAPACITY_MIN}; zero would be a benched key). */
const capacityOrNull = z.number().int().min(KEY_CAPACITY_MIN).nullable();

/**
 * WHY a claim got the width it got — `task.assigned`'s `output.admission` (#6228), so a
 * narrow `stream_budget` names its cause with one join on the task id: the key's
 * `effective_capacity` and which number set it (`source`: a `learned` clamp after a
 * refusal, the plan `declared`, the kind's `default`; each of the three is recorded too),
 * the `inflight` units and the `reserved` hold-back it was read against, whether coding
 * work `contended`, the review `reserve_width` on offer, and the `path` (which grant
 * decided the width). A STORED shape: every field REQUIRED and nullable (`null` = the
 * claim had no such number: unbounded, or no load read). Strict on WRITE only: a reader
 * of `output.admission` parses it NON-strictly (`.passthrough()` or `.strip()`), so a
 * field a later build adds never fails a row this build reads.
 */
export const KeyAdmissionAuditSchema = z
  .object({
    key_id: z.string().min(1),
    lane: z.enum(KeyAdmissionLane),
    path: z.enum(KeyAdmissionPath),
    budget: z.number().int().min(1).nullable(),
    effective_capacity: capacityOrNull,
    source: z.enum(KeyCapacitySource),
    declared: capacityOrNull,
    default: capacityOrNull,
    // The learner never stores under 1 (`readLearnedCapacities`, `@developerz/queue`).
    learned: capacityOrNull,
    inflight: countOrNull,
    reserved: countOrNull,
    reserve_width: capacityOrNull,
    contended: z.boolean().nullable(),
  })
  .strict();
export type KeyAdmissionAudit = z.infer<typeof KeyAdmissionAuditSchema>;

/**
 * The audit record of one admission. `asked` is the {@link admitOnKey} question that
 * admitted it ({@link admitOnKeyInputOf}); `null` = no load read, admitted fail-open.
 * Parsed, so a field outside the schema throws here rather than landing in a row.
 */
export function keyAdmissionAudit(input: {
  readonly keyId: string;
  readonly lane: KeyAdmissionLane;
  readonly capacity: KeyCapacity;
  readonly budget: number | null;
  readonly asked: AdmitOnKeyInput | null;
}): KeyAdmissionAudit {
  const { asked, capacity } = input;
  return KeyAdmissionAuditSchema.parse({
    key_id: input.keyId,
    lane: input.lane,
    path: asked === null ? KeyAdmissionPath.Unobserved : keyAdmissionPath(asked, input.budget),
    budget: input.budget,
    effective_capacity: capacity.effective,
    source: capacity.source,
    declared: capacity.declared,
    default: capacity.default,
    learned: capacity.learned,
    inflight: asked?.inflight ?? null,
    reserved: asked === null ? null : (asked.reserved ?? 0),
    // No reserve on an unbounded key (`path: 'unbounded'`) or off the review lane.
    reserve_width: asked === null || asked.capacity === null ? null : (asked.reserveWidth ?? null),
    contended: asked === null ? null : (asked.contended ?? false),
  });
}

/**
 * A record {@link keyAdmissionAudit} REFUSED — `task.assigned`'s `output.admission_invalid`
 * (#6228), so a claim admitted without its record still says so durably (audit is trust).
 * `issues` are the schema issues as `<path>:<code>` only, never the values: a value is
 * what failed, and could be anything. A STORED shape, strict, every field required.
 */
export const KeyAdmissionInvalidSchema = z
  .object({
    key_id: z.string(),
    lane: z.enum(KeyAdmissionLane).nullable(),
    budget: z.number().int().nullable(),
    issues: z.array(z.string().min(1)).min(1),
  })
  .strict();
export type KeyAdmissionInvalid = z.infer<typeof KeyAdmissionInvalidSchema>;

/** The `task.assigned` output fragment of one admission: its record, or why it has none. */
export type KeyAdmissionOutput =
  | { readonly admission: KeyAdmissionAudit }
  | { readonly admission_invalid: KeyAdmissionInvalid };

/**
 * {@link keyAdmissionAudit}, TOTAL: it never throws, because the claim it describes is
 * already decided and a throw on the claim path would stall dispatch. A refused record
 * becomes `admission_invalid`, built from sanitized inputs so it cannot fail its own
 * schema.
 */
export function keyAdmissionOutput(
  input: Parameters<typeof keyAdmissionAudit>[0],
): KeyAdmissionOutput {
  try {
    return { admission: keyAdmissionAudit(input) };
  } catch (err) {
    const lanes: readonly string[] = Object.values(KeyAdmissionLane);
    const issues =
      err instanceof z.ZodError && err.issues.length > 0
        ? err.issues.map((i) => `${i.path.join('.') || '(root)'}:${i.code}`)
        : ['not_a_schema_issue'];
    return {
      admission_invalid: {
        key_id: String(input.keyId),
        lane: lanes.includes(input.lane) ? input.lane : null,
        budget: Number.isSafeInteger(input.budget) ? input.budget : null,
        issues,
      },
    };
  }
}

/** One ranked candidate the admission walks: the key, its capacity, its live units. */
export type KeyAdmissionCandidate<K extends { readonly id: string }> = {
  readonly key: K;
  readonly capacity: KeyCapacity;
  readonly inflight: number;
  /**
   * Units THIS key holds back for waiting, higher-ranked work that could run on it
   * ({@link reservedForHigherRanks}). Per candidate, never one number for the pool: a
   * task waiting on another key reserves nothing here. Absent ⇒ 0.
   */
  readonly reserved?: number;
};

/**
 * Walk the pool IN ITS RANKED ORDER and admit on the first key with room — the
 * account's own ranking (route, priority, subscription-first) still decides, and a
 * full key only passes the task to the next one. The caller hands in the keys the task
 * MAY run on: under an Agents-page selection that is the selected key(s) alone, so a
 * full selected key is `saturated`, never a walk to an unselected one. Nothing admits ⇒
 * every candidate is named, so the hold says which keys and how full.
 */
export function admitOnPool<K extends { readonly id: string }>(input: {
  readonly lane: KeyAdmissionLane;
  readonly candidates: readonly KeyAdmissionCandidate<K>[];
  /** Other coding work waits: a coding task takes its fair share ({@link admitOnKey}). */
  readonly contended?: boolean;
  /** A review's wave width ({@link reviewAdmissionWidth}). Absent ⇒ 1. */
  readonly reviewWidth?: number;
}):
  | { readonly kind: 'admitted'; readonly index: number; readonly budget: number | null }
  | { readonly kind: 'saturated'; readonly saturated: readonly KeyAdmissionCandidate<K>[] } {
  for (const [index, c] of input.candidates.entries()) {
    const verdict = admitOnKey(admitOnKeyInputOf(input, c));
    if (verdict.admitted) return { kind: 'admitted', index, budget: verdict.budget };
  }
  return { kind: 'saturated', saturated: input.candidates };
}

/**
 * The {@link admitOnKey} question {@link admitOnPool} asks of one candidate — exported
 * so a caller recording WHY a claim got its width ({@link keyAdmissionPath}) asks the
 * very same one. A review's reserve is the key's own ({@link reviewReserveWidth}).
 */
export function admitOnKeyInputOf(
  pool: {
    readonly lane: KeyAdmissionLane;
    readonly contended?: boolean;
    readonly reviewWidth?: number;
  },
  c: Pick<KeyAdmissionCandidate<{ readonly id: string }>, 'capacity' | 'inflight' | 'reserved'>,
): AdmitOnKeyInput {
  return {
    lane: pool.lane,
    capacity: c.capacity.effective,
    inflight: c.inflight,
    ...(c.reserved !== undefined ? { reserved: c.reserved } : {}),
    ...(pool.contended !== undefined ? { contended: pool.contended } : {}),
    ...(pool.reviewWidth !== undefined ? { reviewWidth: pool.reviewWidth } : {}),
    ...reserveOf(pool, c.capacity),
  };
}

/** A review's reserve on this key, when it has one (a bounded key's review lane). */
function reserveOf(
  pool: { readonly lane: KeyAdmissionLane; readonly reviewWidth?: number },
  capacity: KeyCapacity,
): { readonly reserveWidth?: number } {
  if (pool.lane !== KeyAdmissionLane.Review) return {};
  const reserveWidth = reviewReserveWidth(capacity, pool.reviewWidth ?? 1);
  return reserveWidth === null ? {} : { reserveWidth };
}

/**
 * What one key's `inflight` is made of, so an operator can tell real load from a claim
 * the platform failed to release without reading Dragonfly. Built by `inflightDetail`
 * (`@developerz/queue`) from the same claims and liveness read the count is.
 * Invariant: `inflight = task_units + Σ session_units`.
 */
export type KeyInflightDetail = {
  /** Units held by fleet tasks still running (a `tsk_…` claim whose task runs). */
  readonly task_units: number;
  /**
   * Units held by in-cluster sessions (the agents-manager's claims), per session kind:
   * the entity kind of the dispatch that booted it (`pr`, `issue`, `push`, …) or
   * `triage`. Nothing checks these against a liveness row, so they count until their
   * own release or the claim TTL.
   */
  readonly session_units: Readonly<Record<string, number>>;
  /**
   * Task claims whose task is NOT running: a release the platform missed. Excluded from
   * `inflight` (admission ignores them too); each lapses on the claim TTL. Non-zero
   * names a leaking release path.
   */
  readonly unreleased_task_claims: number;
  /**
   * Session claims held longer than any in-cluster session has run
   * (`SESSION_CLAIM_STALE_SEC`, `@developerz/queue`). They ARE in `inflight` — nothing
   * can prove them dead — so non-zero means a session's release was lost (a crashed
   * pod) and the key reads fuller than it is until the claim TTL.
   */
  readonly stale_session_claims: number;
  /** Seconds the oldest claim counted in `inflight` has been held; null when none. */
  readonly oldest_claim_sec: number | null;
};

/** The detail of a key with no live claim. */
export const EMPTY_INFLIGHT_DETAIL: KeyInflightDetail = {
  task_units: 0,
  session_units: {},
  unreleased_task_claims: 0,
  stale_session_claims: 0,
  oldest_claim_sec: null,
};

/** The units a detail accounts for: its running tasks plus every session kind. */
export function inflightOf(detail: KeyInflightDetail): number {
  let units = detail.task_units;
  for (const n of Object.values(detail.session_units)) units += n;
  return units;
}

/** One key's row in every capacity view (`billing capacity`, `status`, `box fleet_status`). */
export type KeyCapacityRow = {
  readonly key_id: string;
  readonly label: string | null;
  readonly provider: string;
  /** `subscription` | `metered` — the stamped kind, else the endpoint's inference. */
  readonly kind: BYOKKeyKind;
  readonly capacity: KeyCapacity;
  /** Units running on the key now — live claims only (a leaked task claim does not count). */
  readonly inflight: number;
  /** What `inflight` is made of, and the claims that look leaked. */
  readonly inflight_detail: KeyInflightDetail;
  /** Tasks held `key_saturated` waiting for this key. */
  readonly queued: number;
};

/** The account's line: "N agents running / M capacity" — `capacity: null` when any key is unbounded. */
export type KeyCapacityTotals = {
  readonly running: number;
  readonly capacity: number | null;
  readonly queued: number;
};

/**
 * Build the per-key rows and the account totals from the reads each surface already
 * makes. PURE and shared, so the billing view, `status` and `fleet_status` cannot
 * disagree about what "full" means. `queued` in the totals counts each waiting task
 * once however many keys it waited on (`queuedTasks`).
 */
export function keyCapacityView(input: {
  readonly keys: readonly (Pick<BYOKKey, 'id' | 'provider' | 'baseUrl' | 'kind' | 'label'> & {
    readonly capacity?: number | null;
  })[];
  readonly learned: Readonly<Record<string, number>>;
  /**
   * Per key, what its live load is made of; a key absent here has no live claim. The
   * row's `inflight` is DERIVED from it, so the count and its breakdown cannot disagree.
   */
  readonly inflightDetail: Readonly<Record<string, KeyInflightDetail>>;
  readonly queued: Readonly<Record<string, number>>;
  readonly queuedTasks: number;
}): { readonly keys: readonly KeyCapacityRow[]; readonly totals: KeyCapacityTotals } {
  const keys = input.keys.map((k): KeyCapacityRow => {
    const detail = input.inflightDetail[k.id] ?? EMPTY_INFLIGHT_DETAIL;
    return {
      key_id: k.id,
      label: k.label ?? null,
      provider: k.provider,
      kind: k.kind ?? (isFlatRateKey(k) ? BYOKKeyKind.Subscription : BYOKKeyKind.Metered),
      capacity: effectiveKeyCapacity(k, input.learned[k.id]),
      inflight: inflightOf(detail),
      inflight_detail: detail,
      queued: input.queued[k.id] ?? 0,
    };
  });
  const unbounded = keys.some((k) => k.capacity.effective === null);
  return {
    keys,
    totals: {
      running: keys.reduce((n, k) => n + k.inflight, 0),
      capacity: unbounded ? null : keys.reduce((n, k) => n + (k.capacity.effective ?? 0), 0),
      queued: input.queuedTasks,
    },
  };
}

/**
 * The learned operating point after the owner MOVES a key's ceiling (`key_capacity_set`
 * on any door: declared set, changed or cleared back to the default). `null` = forget it.
 *
 * Measured 2026-09-27: tesote's z.ai sub (7f2049bc) was declared 16 at ~15:55Z and still
 * read `effective=1 source=learned` at 17:10Z. Its record said 1, taught by a rate-limit
 * cool under the old ceiling of 1 — where it never bound (`effectiveKeyCapacity` reads a
 * learned value only BELOW the ceiling). Under 16 the same 1 bound, and the +1-per-clean-
 * saturated-hour raise would take ~15 h to reach the plan the owner just declared.
 *
 * - The learned value sat at or above the OLD ceiling: it never bound the plan (at most
 *   the review reserve past it, `reviewReserveWidth`), and it says nothing about the new
 *   plan beyond "the old one was full" — forget it; the reserve re-opens at full width
 *   and the next refusal re-teaches it.
 * - It sat BELOW the old ceiling: a rate-limit cool really pulled the key under its plan.
 *   Keep that ratio on the new plan (`floor(learned * to / from)`, at least 1), and forget
 *   it once the result reaches the new ceiling, where it would not bind anyway.
 * - The old ceiling was unbounded (a metered key): no ratio exists; keep the learned
 *   value while it is below the new ceiling.
 * - The new ceiling is unbounded: keep it — it is the only bound a refusal taught.
 *
 * Forgetting is safe in the one direction that matters: if the declared number is too
 * high, the next `rate_limited` cool halves from the units then in flight and AIMD
 * re-learns from the new plan within one congestion episode.
 */
export function rescaleLearnedCapacity(input: {
  readonly learned: number;
  readonly from: number | null;
  readonly to: number | null;
}): number | null {
  const { learned, from, to } = input;
  if (to === null) return learned;
  if (from === null) return learned < to ? learned : null;
  if (learned >= from) return null;
  const scaled = Math.max(1, Math.floor((learned * to) / from));
  return scaled < to ? scaled : null;
}
